import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'

/**
 * The question asked before something the fleet cannot be talked out of.
 *
 * Every screen had grown its own answer to this, and most had grown none at all. Trust rotation asked
 * in a dialog; the tenant list asked in a dialog and made somebody retype a slug; the policy list
 * asked in a panel on the page; the enrollment token list swapped its button for a second button. And
 * retiring a dictionary, removing a dictionary rule or an export destination, changing the credential
 * period, lowering a seat limit, slowing the fleet's check-in, disabling somebody and shortening a
 * retention period asked nothing at all. Four spellings of one idea, and silence everywhere else.
 *
 * The distinction that matters is not "is this destructive" — almost everything an operator does here
 * is — but **whether the fleet finds out**. Retiring a policy tidies a list. Revoking its publication
 * serves a signed tombstone that stops it running on every machine, and no amount of clicking undoes
 * that: the device has already stopped. So the question is asked in proportion. An action somebody can
 * put back by doing the opposite gets a sentence and a button. An action that reaches the fleet gets
 * `confirmPhrase`, and nothing is offered until it has been typed.
 *
 * `confirmPhrase` is not theater. It defeats exactly one thing, and it is the thing that actually
 * happens: a pointer already traveling toward where the confirming button will appear. Typing cannot
 * be done by momentum.
 */
export interface ConfirmDialogProps {
  open: boolean
  title: string
  /** What the confirming button says. Name the act — "Retire and stop them running", not "OK". */
  confirmLabel: string
  onConfirm: () => void
  onClose: () => void
  /** Disables both buttons while the request is in flight, so nothing is asked for twice. */
  busy?: boolean
  /** Paints the confirming button as a warning. Off for a change that can simply be made again. */
  danger?: boolean
  /**
   * Typed exactly before the action is offered. Reserved for the reach-the-fleet cases, because an
   * organization that has to type something every time stops reading what it is typing.
   */
  confirmPhrase?: string
  /**
   * Holds the confirming button closed for a caller's own reason — a field the action needs and has not got.
   * Separate from `busy` so a form that is merely incomplete does not read as one that is working.
   */
  confirmDisabled?: boolean
  /** What this does, and what it costs. The caller writes it; there is no generic version worth having. */
  children: React.ReactNode
  'data-testid'?: string
}

export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  onConfirm,
  onClose,
  busy,
  danger,
  confirmPhrase,
  confirmDisabled,
  children,
  ...rest
}: ConfirmDialogProps) {
  const testId = rest['data-testid'] ?? 'confirm'
  const [typed, setTyped] = useState('')

  // Cleared on open rather than on close. A dialog closed by Escape mid-type would otherwise reopen
  // with the phrase already satisfied, which is the one state this control exists to prevent.
  useEffect(() => {
    if (open) setTyped('')
  }, [open])

  const satisfied = !confirmPhrase || typed.trim() === confirmPhrase

  return (
    <Dialog open={open} title={title} onClose={onClose} data-testid={testId}>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (satisfied && !busy && !confirmDisabled) onConfirm()
        }}
      >
        <div className="confirm-body">{children}</div>

        {confirmPhrase && (
          <div className="field">
            <label htmlFor={`${testId}-phrase-input`}>
              Type <span className="mono">{confirmPhrase}</span> to confirm
            </label>
            <input
              id={`${testId}-phrase-input`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              data-testid={`${testId}-phrase`}
            />
          </div>
        )}

        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={busy} data-testid={`${testId}-cancel`}>
            Cancel
          </button>
          <button
            type="submit"
            className={danger ? 'danger' : 'primary'}
            disabled={busy || !satisfied || confirmDisabled}
            data-testid={`${testId}-accept`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
